import React, { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, RotateCcw, Download, Eye } from "lucide-react";
import { trpc } from "@/lib/trpc";

interface ImportHistoryItem {
  id: number;
  fileName: string;
  status: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  importedRows: number;
  createdAt: Date;
  userId?: number;
  fileUrl?: string;
  errorsSummary?: any;
  importedSample?: any;
}

interface ImportHistoryListProps {
  channelId?: number;
  companyId?: number;
  onReenviar?: (importId: number) => void;
  onVerDetalhes?: (importId: number) => void;
}

export const ImportHistoryList: React.FC<ImportHistoryListProps> = ({
  channelId,
  companyId,
  onReenviar,
  onVerDetalhes,
}) => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const limit = 10;

  // Buscar histórico
  const { data: historyData, isLoading, error } = trpc.imports.listHistory.useQuery(
    {
      channelId,
      companyId,
      page,
      limit,
    },
    {
      enabled: !!companyId, // Só busca se companyId está definido
    }
  );

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: any }> = {
      PROCESSADA: { label: "Processada", variant: "secondary" },
      PROCESSADA_COM_ERROS: { label: "Com Erros", variant: "destructive" },
      PROCESSADA_COM_SUCESSO: { label: "Sucesso", variant: "default" },
      CONFIRMADA: { label: "Confirmada", variant: "default" },
      COM_ERROS: { label: "Com Erros", variant: "destructive" },
      BLOQUEADA: { label: "Bloqueada", variant: "outline" },
    };

    const config = statusMap[status] || { label: status, variant: "secondary" };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const calculateSuccessRate = (item: ImportHistoryItem) => {
    if (item.totalRows === 0) return 0;
    return Math.round((item.validRows / item.totalRows) * 100);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="p-4 animate-pulse">
            <div className="h-20 bg-gray-200 rounded"></div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-700 text-sm">Erro ao carregar histórico: {error.message}</p>
      </Card>
    );
  }

  if (!historyData?.data || historyData.data.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-gray-500">Nenhuma importação encontrada</p>
        {historyData?.message && <p className="text-sm text-gray-400 mt-2">{historyData.message}</p>}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Lista de importações */}
      {historyData.data.map((item: ImportHistoryItem) => (
        <Card key={item.id} className="overflow-hidden">
          {/* Header do card */}
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-sm">{item.fileName}</h3>
                  {getStatusBadge(item.status)}
                </div>
                <p className="text-xs text-gray-500">
                  {format(new Date(item.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </p>
              </div>
              <button className="p-1 hover:bg-gray-100 rounded">
                {expandedId === item.id ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Resumo numérico */}
            <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
              <div className="bg-blue-50 p-2 rounded">
                <p className="text-gray-600">Total</p>
                <p className="font-semibold text-blue-700">{item.totalRows}</p>
              </div>
              <div className="bg-green-50 p-2 rounded">
                <p className="text-gray-600">Válidas</p>
                <p className="font-semibold text-green-700">{item.validRows}</p>
              </div>
              <div className="bg-red-50 p-2 rounded">
                <p className="text-gray-600">Inválidas</p>
                <p className="font-semibold text-red-700">{item.invalidRows}</p>
              </div>
              <div className="bg-purple-50 p-2 rounded">
                <p className="text-gray-600">Taxa</p>
                <p className="font-semibold text-purple-700">{calculateSuccessRate(item)}%</p>
              </div>
            </div>
          </div>

          {/* Conteúdo expandido */}
          {expandedId === item.id && (
            <div className="border-t p-4 bg-gray-50 space-y-3">
              {/* Ações */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onVerDetalhes?.(item.id)}
                  className="gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Ver Detalhes
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReenviar?.(item.id)}
                  className="gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reenviar
                </Button>
                {item.fileUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(item.fileUrl, "_blank")}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Arquivo Original
                  </Button>
                )}
              </div>

              {/* Resumo de erros se houver */}
              {item.invalidRows > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm">
                  <p className="font-semibold text-yellow-800 mb-1">⚠️ {item.invalidRows} linhas com erro</p>
                  <p className="text-yellow-700">Clique em "Ver Detalhes" para consultar os erros</p>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {/* Paginação */}
      {historyData.total > limit && (
        <div className="flex justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Anterior
          </Button>
          <span className="text-sm text-gray-600 flex items-center px-3">
            Página {page} de {Math.ceil(historyData.total / limit)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(historyData.total / limit)}
            onClick={() => setPage(page + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
};
